<template>
  <a-modal
    :visible="visible"
    :footer="null"
    :closable="false"
    :mask-closable="false"
    :width="480"
    class="save-success-modal"
    @cancel="handleClose"
  >
    <div class="modal-content">
      <!-- Success Icon -->
      <div class="success-icon">
        <a-icon type="check" />
      </div>

      <!-- Title -->
      <h2 class="modal-title">{{ $t("CampaignPublishedSuccessfully") }}</h2>

      <!-- Subtitle -->
      <p class="modal-subtitle">
        <strong>"{{ queterInfo.campaign_name }}"</strong>
        {{ $t("AddedToExecutionQueue") }}
      </p>

      <!-- Execution Forecast -->
      <div class="forecast-section">
        <h3 class="forecast-title">
          <a-icon type="bar-chart" /> {{ $t("ExecutionForecast") }}
        </h3>
        <div class="forecast-item" v-if="queterInfo.campaign_list">
          <span class="forecast-label">{{ $t("QueuePosition") }}</span>
          <span class="forecast-value"
            >No. {{ getPos + 1 }} ({{ $t("outOf") }}
            {{ queterInfo.campaign_list.length }} {{ $t("Campaigns") }})</span
          >
        </div>
        <div class="forecast-item">
          <span class="forecast-label">{{ $t("AllocationMode") }}</span>
          <span class="forecast-value">{{ allocationModeText }}</span>
        </div>
        <div class="forecast-item">
          <span class="forecast-label">{{ $t("DailyLimit") }}</span>
          <span class="forecast-value highlight" v-if="queterInfo.linkedin_info"
            >{{ queterInfo.linkedin_info.request_limit }}
            {{ $t("requestsPerDay") }}</span
          >
        </div>
      </div>

      <!-- Current Execution Queue -->
      <div class="queue-section">
        <h3 class="queue-title">{{ $t("CurrentExecutionQueue") }}:</h3>
        <div class="queue-list" v-if="queterInfo.campaign_list">
          <div
            v-for="(campaign, index) in queterInfo.campaign_list"
            :key="index"
            class="queue-item"
            :class="{
              current: getPos == index ? true : false,
            }"
          >
            <div class="queue-left">
              <span
                class="queue-number"
                :class="{
                  top: index === 0,
                  second: index === 1,
                  third: index === 2,
                }"
                >{{ index + 1 }}</span
              >
              <span class="campaign-name">{{ campaign.campaign_name }}</span>
              <span v-if="getPos == index" class="current-badge">⭐</span>
            </div>
            <div
              class="queue-right"
              v-if="queterInfo.linkedin_info.allocation_mode == 1"
            >
              <span
                v-if="campaign.today_plan_kind == 'needed'"
                class="needed-badge"
                ><strong>{{ campaign.today_send_count }}</strong>
                {{ $t("needed") }}</span
              >
              <span v-if="campaign.today_plan_kind == 'none'">--</span>
              <span
                v-if="campaign.today_plan_kind == 'today'"
                class="today-badge"
                ><strong>{{ campaign.today_send_count }}</strong>
                {{ $t("today") }}</span
              >
              <span
                v-if="campaign.send_status == 3"
                class="status-badge done"
                >{{ $t("done") }}</span
              >
              <span
                v-if="campaign.send_status == 1"
                class="status-badge in-progress"
                >{{ $t("inProgress") }}</span
              >
              <template
                v-if="campaign.send_status == 2 || campaign.send_status == 4"
              >
                <span class="status-badge waiting">{{ $t("waiting") }}</span>
              </template>
            </div>
            <div
              class="queue-right"
              v-if="queterInfo.linkedin_info.allocation_mode == 2"
            >
              <span v-if="campaign.send_status !== 3" class="needed-badge"
                ><strong>{{ campaign.today_send_count }}</strong>
                {{ $t("needed") }}</span
              >

              <span
                v-if="campaign.send_status == 3"
                class="status-badge done"
                >{{ $t("done") }}</span
              >
              <span
                v-if="campaign.send_status == 1"
                class="status-badge in-progress"
                >{{ $t("inProgress") }}</span
              >
              <template v-if="campaign.send_status == 2">
                <span class="status-badge waiting">{{ $t("waiting") }}</span>
              </template>
              <template v-if="campaign.send_status == 4">
                <span class="status-badge waiting">{{ $t("waiting") }}</span>
              </template>
            </div>
          </div>
        </div>
      </div>

      <!-- Sequential Notice -->
      <div
        v-if="
          queterInfo.linkedin_info &&
          queterInfo.linkedin_info.allocation_mode == 1
        "
        class="sequential-notice"
      >
        <div class="notice-header">
          <a-icon
            type="exclamation-circle"
            theme="filled"
            class="notice-icon"
          />
          <span class="notice-title">{{
            $t("SequentialExecutionNotice")
          }}</span>
        </div>
        <p class="notice-content">
          {{ $t("SequentialNoticeModalContent") }}
        </p>
        <div class="notice-tip">
          <a-icon type="bulb" />
          <span>{{ $t("SequentialNoticeModalTip") }}</span>
        </div>
      </div>

      <div class="equal-notice" v-else>
        <span class="notice-icon"> 💡</span>
        <!-- <a-icon type="bulb" theme="filled"  /> -->
        <div class="right-col">
          <div class="notice-header">
            <span class="notice-title"> {{ $t("EqualExecutionNotice") }}</span>
          </div>
          <p class="notice-content">
            {{ $t("EqualNoticeContent") }}
          </p>
        </div>
      </div>

      <!-- Action Button -->
      <a-button type="primary" class="action-btn" @click="handleManage">
        {{ $t("GoToManageCampaignList") }}
      </a-button>

      <!-- Footer Note -->
      <p class="footer-note" v-if="queterInfo.allocation_mode == 1">
        {{ $t("ModifySequenceNote") }}
      </p>
      <p v-else class="footer-note">
        {{ $t("ModifyAllocationNote") }}
      </p>
    </div>
  </a-modal>
</template>

<script>
import i18n from "./i18n";

export default {
  name: "SaveSuccessModal",
  i18n,
  props: {
    queterInfo: {
      type: Object,
      default: () => {},
    },
    visible: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    allocationModeText() {
      return this.queterInfo.linkedin_info &&
        this.queterInfo.linkedin_info.allocation_mode == 1
        ? this.$t("SequentialExecution")
        : this.$t("EqualDistribution");
    },
    getPos() {
      let pos = 0;
      if (this.queterInfo.campaign_list) {
        this.queterInfo.campaign_list.some((item, index) => {
          if (item.campaign_name == this.queterInfo.campaign_name) {
            pos = index;
          }
        });
      }

      return pos;
    },
  },
  methods: {
    getCampaignStatus(campaign) {
      if (campaign.status) return campaign.status;
      if (campaign.planNeeded) return "In Progress";
      if (campaign.today) return "Waiting";
      return "Waiting";
    },
    handleClose() {
      this.$emit("close");
    },
    handleManage() {
      this.$emit("manage");
    },
  },
};
</script>

<style scoped lang="less">
.save-success-modal {
  ::v-deep .ant-modal-content {
    border-radius: 12px;
    overflow: hidden;
  }

  ::v-deep .ant-modal-body {
    padding: 0;
  }
}

.modal-content {
  padding: 32px 24px;
  text-align: center;
}

.success-icon {
  width: 64px;
  height: 64px;
  background: #12b981;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;

  i {
    color: #333;
    font-size: 32px;
  }
}

.modal-title {
  font-size: 22px;
  font-weight: 600;
  color: #1f1f1f;
  margin-bottom: 8px;
}

.modal-subtitle {
  font-size: 14px;
  color: #666;
  margin-bottom: 24px;

  strong {
    color: #1f1f1f;
  }
}

.forecast-section {
  background: #f9fafb;
  border-radius: 10px;
  padding: 16px 20px;
  margin-bottom: 20px;
  text-align: left;

  .forecast-title {
    font-size: 14px;
    font-weight: 600;
    color: #1f1f1f;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 6px;

    i {
      color: #12b981;
    }
  }

  .forecast-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px dashed #e5e7eb;

    &:last-child {
      border-bottom: none;
    }

    .forecast-label {
      font-size: 13px;
      color: #6b7280;
    }

    .forecast-value {
      font-size: 13px;
      font-weight: 500;
      color: #1f1f1f;

      &.highlight {
        color: #12b981;
      }
    }
  }
}

.queue-section {
  text-align: left;
  margin-bottom: 20px;

  .queue-title {
    font-size: 13px;
    font-weight: 600;
    color: #1f1f1f;
    margin-bottom: 12px;
  }

  .queue-list {
    .queue-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 10px 12px;
      border-radius: 8px;
      margin-bottom: 8px;
      background: #f9fafb;

      &.current {
        background: #e8feef;
        border: 1px solid #12b981;
      }

      .queue-left {
        display: flex;
        align-items: center;
        gap: 10px;

        .queue-number {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #f0f0f0;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 600;
          color: #666;

          &.top {
            background: #12b981;
            color: #fff;
          }

          &.second {
            background: #f59e0c;
            color: #fff;
          }

          &.third {
            background: #9da3af;
            color: #fff;
          }
        }

        .campaign-name {
          font-size: 13px;
          font-weight: 500;
          color: #1f1f1f;
        }

        .current-badge {
          font-size: 12px;
        }
      }

      .queue-right {
        display: flex;
        align-items: center;
        gap: 8px;

        .needed-badge,
        .today-badge {
          font-size: 12px;
          color: #6b7280;

          strong {
            color: #12b981;
            font-size: 14px;
          }
        }

        .today-badge strong {
          color: #f59e0c;
        }

        .status-badge {
          padding: 2px 10px;
          border-radius: 100px;
          font-size: 11px;
          font-weight: 500;

          &.done {
            background: #f3f4f6;
            color: #9ca3af;
          }

          &.in-progress {
            background: #d1fae6;
            color: #059669;
          }

          &.waiting {
            background: #f3f4f6;
            color: #6b7280;
          }
        }
      }
    }
  }
}
.equal-notice {
  background: #edf4ff;
  border: 1px solid #5996f7;
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 20px;
  text-align: left;
  display: flex;
  .notice-icon {
    font-size: 16px;
    margin-right: 5px;
  }
  .notice-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;

    .notice-title {
      font-weight: 700;
      font-size: 13px;
      color: #1f40b0;
    }
  }

  .notice-content {
    font-size: 12px;
    color: #1f40b0;
    line-height: 1.5;
  }
}

.sequential-notice {
  background: linear-gradient(to right, #fef3c2, #fee790);
  border: 1px solid #f9b853;
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 20px;
  text-align: left;

  .notice-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;

    .notice-icon {
      color: #f59e0c;
      font-size: 16px;
    }

    .notice-title {
      font-weight: 600;
      font-size: 13px;
      color: #92400d;
    }
  }

  .notice-content {
    font-size: 12px;
    color: #78350f;
    line-height: 1.5;
    margin-bottom: 10px;
  }

  .notice-tip {
    background: rgba(255, 255, 255, 0.5);
    border-radius: 6px;
    padding: 8px 12px;
    font-size: 11px;
    color: #92400d;

    i {
      color: #f59e0c;
      margin-right: 5px;
    }
  }
}

.action-btn {
  width: 100%;
  height: 44px;
  background: #12b981;
  border-color: #12b981;
  font-size: 15px;
  font-weight: 500;
  border-radius: 8px;
  margin-bottom: 12px;

  &:hover {
    background: #10b981;
    border-color: #10b981;
  }
}

.footer-note {
  font-size: 11px;
  color: #f59e0c;
  line-height: 1.4;
  margin: 0;
}
</style>
